const getVideoData = root => Array.from(root.querySelectorAll('[data-video-url]')).map(item => ({
    title: item.dataset.videoTitle || '',
    tag: item.dataset.videoTag || '',
    url: item.dataset.videoUrl || '',
}));

const getAutoplayUrl = (url, options = {}) => {
    if (!url) {
        return '';
    }

    const params = ['autoplay=1', 'rel=0', 'modestbranding=1', 'playsinline=1'];

    if (options.muted) {
        params.push('mute=1', 'muted=1');
    }

    if (options.trackEnded) {
        params.push('enablejsapi=1', 'api=1');
    }

    return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`;
};

const createIframe = (video, options = {}) => {
    const iframe = document.createElement('iframe');
    iframe.src = getAutoplayUrl(video.url, options);
    iframe.title = video.title;
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    return iframe;
};

const parseVideoMessage = event => {
    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

    if (!data || typeof data !== 'object') {
        return null;
    }

    if (data.event === 'ended') {
        return 'ended';
    }

    if (data.event === 'infoDelivery' && data.info && data.info.playerState === 0) {
        return 'ended';
    }

    if (data.event === 'onStateChange' && data.info === 0) {
        return 'ended';
    }

    return null;
};

const listenForVideoEnd = (iframe, callback) => {
    const handleMessage = event => {
        if (event.source !== iframe.contentWindow) {
            return;
        }

        let messageType = null;

        try {
            messageType = parseVideoMessage(event);
        } catch (error) {
            messageType = null;
        }

        if (messageType !== 'ended') {
            return;
        }

        window.removeEventListener('message', handleMessage);
        callback();
    };

    window.addEventListener('message', handleMessage);

    iframe.addEventListener('load', () => {
        if (!iframe.contentWindow) {
            return;
        }

        iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'ended' }), '*');
    });

    return () => window.removeEventListener('message', handleMessage);
};

const setupModal = root => {
    const modal = root.querySelector('[data-video-modal]');

    if (!modal) {
        return null;
    }

    document.body.appendChild(modal);

    const videos = getVideoData(root);
    const title = modal.querySelector('[data-video-modal-title]');
    const tag = modal.querySelector('[data-video-modal-tag]');
    const frame = modal.querySelector('[data-video-modal-frame]');
    const dots = modal.querySelector('[data-video-modal-dots]');
    const prev = modal.querySelector('[data-video-modal-prev]');
    const next = modal.querySelector('[data-video-modal-next]');
    const closeButtons = modal.querySelectorAll('[data-video-modal-close]');
    let activeIndex = 0;

    if (!videos.length || !title || !tag || !frame || !prev || !next) {
        return null;
    }

    const renderDots = () => {
        if (!dots) {
            return;
        }

        dots.innerHTML = '';

        videos.forEach((video, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = index === activeIndex ? 'is-active' : '';
            dot.setAttribute('aria-label', `Open ${video.title}`);
            dot.addEventListener('click', () => open(index));
            dots.appendChild(dot);
        });
    };

    const render = () => {
        const video = videos[activeIndex];

        if (!video) {
            return;
        }

        title.textContent = video.title;
        tag.textContent = video.tag;
        tag.hidden = !video.tag;
        frame.innerHTML = '';
        frame.appendChild(createIframe(video));
        prev.disabled = activeIndex === 0;
        next.disabled = activeIndex === videos.length - 1;
        renderDots();
    };

    const open = index => {
        activeIndex = index;
        render();
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('rs-video-module-modal-open');
    };

    const close = () => {
        modal.setAttribute('aria-hidden', 'true');
        frame.innerHTML = '';
        document.body.classList.remove('rs-video-module-modal-open');
    };

    prev.addEventListener('click', () => {
        if (activeIndex > 0) {
            open(activeIndex - 1);
        }
    });

    next.addEventListener('click', () => {
        if (activeIndex < videos.length - 1) {
            open(activeIndex + 1);
        }
    });

    closeButtons.forEach(button => {
        button.addEventListener('click', close);
    });

    document.addEventListener('keydown', event => {
        if (modal.getAttribute('aria-hidden') === 'true') {
            return;
        }

        if (event.key === 'Escape') {
            close();
        }

        if (event.key === 'ArrowLeft' && activeIndex > 0) {
            open(activeIndex - 1);
        }

        if (event.key === 'ArrowRight' && activeIndex < videos.length - 1) {
            open(activeIndex + 1);
        }
    });

    return {
        open,
    };
};

const isFeaturedMobileLayout = () => window.matchMedia('(max-width: 1023px)').matches;

const setupFeaturedPlaylist = root => {
    const playlist = root.querySelector('[data-featured-video-playlist]');

    if (!playlist) {
        return;
    }

    const modal = setupModal(root);
    const videos = getVideoData(root);
    const features = Array.from(root.querySelectorAll('[data-featured-video]'));
    const triggers = Array.from(root.querySelectorAll('[data-featured-trigger]'));
    const autoplayNext = playlist.dataset.featuredAutoplayNext === 'true';
    let activeIndex = 0;
    let removeFeaturedEndListener = null;

    const stopPlayback = () => {
        if (removeFeaturedEndListener) {
            removeFeaturedEndListener();
            removeFeaturedEndListener = null;
        }

        features.forEach(feature => {
            const iframe = feature.querySelector('iframe');

            if (iframe) {
                iframe.remove();
            }

            feature.classList.remove('is-playing');
        });
    };

    const setActive = index => {
        activeIndex = index;
        stopPlayback();

        features.forEach((feature, featureIndex) => {
            const isActive = featureIndex === index;
            feature.classList.toggle('is-active', isActive);
            feature.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        triggers.forEach((trigger, triggerIndex) => {
            const isActive = triggerIndex === index;
            trigger.classList.toggle('is-active', isActive);
            trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
    };

    const playFeatured = (index, options = {}) => {
        const feature = features[index];
        const video = videos[index];

        stopPlayback();

        if (!feature || !video) {
            return;
        }

        const iframe = createIframe(video, {
            ...options,
            trackEnded: autoplayNext,
        });

        feature.appendChild(iframe);
        feature.classList.add('is-playing');

        if (autoplayNext && index < videos.length - 1) {
            removeFeaturedEndListener = listenForVideoEnd(iframe, () => {
                const nextIndex = index + 1;
                const shouldMuteNext = options.muted || isFeaturedMobileLayout();

                setActive(nextIndex);
                playFeatured(nextIndex, { muted: shouldMuteNext });
            });
        }
    };

    triggers.forEach(trigger => {
        trigger.addEventListener('click', event => {
            const index = parseInt(trigger.dataset.videoIndex, 10);
            const shouldPlay = isFeaturedMobileLayout() || event.target.closest('.block-featured-video-playlist__episode-play');

            setActive(index);

            if (shouldPlay) {
                playFeatured(index, { muted: isFeaturedMobileLayout() });
            }
        });
    });

    features.forEach(feature => {
        const play = feature.querySelector('[data-featured-play]');
        const expand = feature.querySelector('[data-video-expand]');

        feature.addEventListener('click', event => {
            if (!isFeaturedMobileLayout() || event.target.closest('button') || feature.querySelector('iframe')) {
                return;
            }

            playFeatured(parseInt(feature.dataset.videoIndex, 10), { muted: true });
        });

        if (play) {
            play.addEventListener('click', () => {
                playFeatured(activeIndex, { muted: isFeaturedMobileLayout() });
            });
        }

        if (expand && modal) {
            expand.addEventListener('click', () => {
                modal.open(activeIndex);
            });
        }
    });
};

const getVisibleCount = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
        return 3;
    }

    if (window.matchMedia('(min-width: 768px)').matches) {
        return 2;
    }

    return 1;
};

const setupVideoCarousel = root => {
    const carousel = root.querySelector('[data-video-carousel]');

    if (!carousel) {
        return;
    }

    const modal = setupModal(root);
    const cards = Array.from(root.querySelectorAll('[data-video-card]'));
    const counters = Array.from(root.querySelectorAll('[data-video-carousel-counter]'));
    const prev = root.querySelector('[data-video-carousel-prev]');
    const next = root.querySelector('[data-video-carousel-next]');
    const dots = root.querySelector('[data-video-carousel-dots]');
    const grid = root.querySelector('.block-video-carousel__grid');
    let startIndex = 0;
    let visibleCount = getVisibleCount();
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerActive = false;
    let suppressCardClick = false;

    const getMaxStart = () => Math.max(cards.length - visibleCount, 0);

    const goTo = index => {
        startIndex = Math.max(Math.min(index, getMaxStart()), 0);
        render();
    };

    const renderDots = () => {
        if (!dots) {
            return;
        }

        dots.innerHTML = '';

        for (let index = 0; index <= getMaxStart(); index += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = index === startIndex ? 'is-active' : '';
            dot.setAttribute('aria-label', `Show videos starting at ${index + 1}`);
            dot.addEventListener('click', () => goTo(index));
            dots.appendChild(dot);
        }
    };

    const render = () => {
        visibleCount = getVisibleCount();
        startIndex = Math.min(startIndex, getMaxStart());

        if (grid) {
            grid.style.setProperty('--video-carousel-index', startIndex);
        }

        cards.forEach((card, index) => {
            card.classList.toggle('is-visible', index >= startIndex && index < startIndex + visibleCount);
        });

        counters.forEach(counter => {
            counter.innerHTML = `${startIndex + 1}&ndash;${Math.min(startIndex + visibleCount, cards.length)} <span>/ ${cards.length}</span>`;
        });

        prev.disabled = startIndex === 0;
        next.disabled = startIndex >= getMaxStart();
        renderDots();
    };

    cards.forEach(card => {
        card.addEventListener('click', event => {
            if (suppressCardClick) {
                event.preventDefault();
                suppressCardClick = false;
                return;
            }

            if (modal) {
                modal.open(parseInt(card.dataset.videoIndex, 10));
            }
        });
    });

    prev.addEventListener('click', () => {
        goTo(startIndex - 1);
    });

    next.addEventListener('click', () => {
        goTo(startIndex + 1);
    });

    if (grid) {
        grid.addEventListener('pointerdown', event => {
            if (getVisibleCount() !== 1) {
                return;
            }

            pointerActive = true;
            suppressCardClick = false;
            pointerStartX = event.clientX;
            pointerStartY = event.clientY;
        });

        grid.addEventListener('pointerup', event => {
            if (!pointerActive) {
                return;
            }

            pointerActive = false;

            const deltaX = event.clientX - pointerStartX;
            const deltaY = event.clientY - pointerStartY;

            if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
                return;
            }

            suppressCardClick = true;
            setTimeout(() => {
                suppressCardClick = false;
            }, 250);
            goTo(startIndex + (deltaX < 0 ? 1 : -1));
        });

        grid.addEventListener('pointercancel', () => {
            pointerActive = false;
        });
    }

    window.addEventListener('resize', render);
    render();
};

export default {
    initFeatured(selector = '[data-featured-video-playlist]') {
        Array.from(document.querySelectorAll(selector)).forEach(playlist => {
            setupFeaturedPlaylist(playlist.closest('[data-video-module]'));
        });
    },
    initCarousel(selector = '[data-video-carousel]') {
        Array.from(document.querySelectorAll(selector)).forEach(carousel => {
            setupVideoCarousel(carousel.closest('[data-video-module]'));
        });
    },
};
